import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { componentDestroyed } from '@w11k/ngx-componentdestroyed';
import { PatientService } from 'src/app/service/patient-service/patient.service';
import { MemberService } from 'src/app/service/member-service/member.service';
import { sweetAlert } from 'src/app/utils/swal';
import * as moment from 'moment-timezone';
import { encryptField, decryptField } from 'src/app/utils/decrypt-utils';

function budgetRangeValidator(group: FormGroup): ValidationErrors | null {
  const minControl = group.get('budget_min');
  const maxControl = group.get('budget_max');
  const min = minControl ? minControl.value : null;
  const max = maxControl ? maxControl.value : null;
  if (min !== null && max !== null && min !== '' && max !== '' && Number(min) > Number(max)) {
    return { budgetRangeInvalid: true };
  }
  return null;
}

function dobValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) {
    return null;
  }
  const date = moment(control.value);
  if (!date.isValid()) {
    return { invalidDate: true };
  }
  const today = moment().startOf('day');
  if (date.isAfter(today)) {
    return { futureDate: true };
  }
  const age = today.diff(date, 'years');
  if (age < 18) {
    return { underAge: true };
  }
  return null;
}

@Component({
  selector: 'app-add-patient',
  templateUrl: './add-patient.component.html',
  styleUrls: ['./add-patient.component.css']
})
export class AddPatientComponent implements OnInit, OnDestroy {
  patientForm: FormGroup;
  submitted = false;
  isRequestInprogress = false;
  showCreateAssist = false;
  levelOfCares: any[] = [];
  isLoadingData = false;
  memberSummary: any = null;
  preIntakeData: any = null;
  isLoadingPreIntake = false;
  refPatientData:any;

  maxDob: Date = moment().subtract(18, 'years').toDate();
  accommodationTypes: any[] = [];
  isLoadingAccommodationTypes = false;

  relationships: any;
  isLoadingRelationships = false;

  isEditMode = false;
  patientId = '';

  transfers: any;
  ambulation: any;
  bathing: any;
  eating: any;
  mentalStatus: any;
  incontinencecare: any;
  diabetes: any;
  dressing: any;
  specialcareNeeds: any;
  dietRestrictions: any;
  entitlements: any;

  dressingTypeOptions = [
    'Upper Body',
    'Lower Body'
  ];

  hospiceOptions = [
    'Yes',
    'No'
  ];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private patientService: PatientService,
    private memberService: MemberService
  ) { }

  ngOnInit() {
    this.initForm();
    this.loadPreIntakeData();
    this.loadRelationships();
    this.loadLevelOfCares();
    this.loadAccommodationTypes();

    const clientId = this.activatedRoute.snapshot.paramMap.get('clientId');
    if (clientId) {
      this.getPatientByRef(clientId);
      this.loadMemberDetails(clientId);
    }

    const patientId = this.activatedRoute.snapshot.paramMap.get('id');
    if (patientId) {
      this.isEditMode = true;
      this.patientId = patientId;
      this.loadPatientDetails(patientId);
    }
  }

  getPatientByRef(clientId: string) {
    this.patientService.getPatientByRef(clientId)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (data: any) => {
          if (data) {
            this.refPatientData = data;
            this.showCreateAssist = true;
            this.patientId = String(data.patient_id);
            if(this.patientId){
              this.isEditMode = true;
            }
            this.populateFormForEdit(data);
          } else {
            this.showCreateAssist = false;
          }
        },
        (err) => {
          this.showCreateAssist = false;
          console.error('Error fetching patient by ref:', err);
        }
      );
  }

  loadMemberDetails(clientId: string) {
    this.memberService.getMemberById(clientId)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (data) => {
          if (data) {
            this.memberSummary = data;
            this.populateFormWithMember(data);
          }
        },
        (err) => {
          sweetAlert('Error', 'Failed to retrieve member details', 'warning', 'OK');
        }
      );
  }

  populateFormWithMember(data: any) {
    let genderVal = data.gender || '';
    if (genderVal) {
      genderVal = genderVal.charAt(0).toUpperCase() + genderVal.slice(1).toLowerCase();
      if (genderVal === 'Non-binary') {
        genderVal = 'Non Binary';
      }
    }

    this.patientForm.patchValue({
      first_name: data.firstName || '',
      last_name: data.lastName || '',
      email: data.email || '',
      phone: data.contactNumber || data.cellPhone || '',
      dob: data.dob ? moment(data.dob).toDate() : '',
      date_of_birth: data.dob ? moment(data.dob).format('YYYY-MM-DD') : '',
      gender: genderVal,
      height_feet: data.height_feet !== undefined && data.height_feet !== null ? data.height_feet : 0,
      height_inches: data.height_inches !== undefined && data.height_inches !== null ? data.height_inches : 0,
      weight: data.weight !== undefined && data.weight !== null ? data.weight : '0.00'
    });
  }

  loadPatientDetails(id: string) {
    this.isLoadingData = true;
    this.patientService.getIntakeById(id)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (intake: any) => {
          this.isLoadingData = false;
          if (intake) {
            this.patientId = String(intake.patient_id);
            this.populateFormForEdit(intake);
          }
        },
        (err) => {
          this.isLoadingData = false;
          sweetAlert('Error', 'Failed to retrieve patient details', 'warning', 'OK');
        }
      );
  }

  populateFormForEdit(intake: any) {
    const patient = intake.e_patients || {};
    const rp = patient.e_patientresponsibleparty && patient.e_patientresponsibleparty.length ? patient.e_patientresponsibleparty[0] : {};

    const needs = intake.e_patientintakespecialcare || [];
    const diets = intake.e_patientintakedietrestriction || [];
    const ents = intake.e_patientintakeentitlement || [];

    const dobDecrypted = patient.date_of_birth ? decryptField(patient.date_of_birth) : '';

    this.patientForm.patchValue({
      relationship_id: (this.refPatientData && this.refPatientData.e_patientresponsibleparty) ? this.refPatientData.e_patientresponsibleparty.relationship_id : rp.relationship_id || '',
      first_name: decryptField(patient.first_name),
      last_name: decryptField(patient.last_name),
      dob: dobDecrypted ? moment(dobDecrypted).toDate() : '',
      date_of_birth: dobDecrypted || '',
      gender: patient.gender || '',
      email: patient.email || '',
      phone: decryptField(patient.phone),
      levelofcare_id: intake.levelofcare_id || '',
      annual_income: decryptField(intake.annual_income),
      budget_type: intake.budget_type ? (intake.budget_type.charAt(0).toUpperCase() + intake.budget_type.slice(1).toLowerCase()) : '',
      budget_min: intake.budget_min ? Math.round(Number(intake.budget_min)) : '',
      budget_max: intake.budget_max ? Math.round(Number(intake.budget_max)) : '',
      height_feet: intake.height_feet !== undefined && intake.height_feet !== null ? intake.height_feet : 0,
      height_inches: intake.height_inches !== undefined && intake.height_inches !== null ? intake.height_inches : 0,
      weight: intake.weight !== undefined && intake.weight !== null ? intake.weight : '0.00',
      accommodation_type_id: intake.accommodation_type_id || '',
      transfers: intake.transfers || '',
      ambulation: intake.ambulation || '',
      bathing: intake.bathing || '',
      eating: intake.eating || '',
      mental_status: intake.mental_status || '',
      incontinence_care_bowel: intake.incontinence_care_bowel || '',
      incontinence_care_bladder: intake.incontinence_care_bladder || '',
      diabetes: intake.diabetes || '',
      dressing: intake.dressing || '',
      dressing_type: intake.dressing_type ? intake.dressing_type.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ') : '',
      hospice_imminent: intake.hospice_imminent ? (intake.hospice_imminent.charAt(0).toUpperCase() + intake.hospice_imminent.slice(1).toLowerCase()) : '',
      diagnosis: decryptField(intake.diagnosis),
      medication_notes: decryptField(intake.medication_notes),
      additional_comments: decryptField(intake.additional_comments),
      agent_show_pii: intake.agent_show_pii === 1,

      need_oxygen: needs.some(n => n.intakespecialcareneeds_id === 1),
      need_catheter: needs.some(n => n.intakespecialcareneeds_id === 2),
      need_catheter_self: needs.some(n => n.intakespecialcareneeds_id === 3),
      need_bedsore: needs.some(n => n.intakespecialcareneeds_id === 4),
      need_colostomy: needs.some(n => n.intakespecialcareneeds_id === 5),
      need_colostomy_self: needs.some(n => n.intakespecialcareneeds_id === 6),

      diet_diabetic: diets.some(d => d.intakedietrestrictions_id === 1),
      diet_low_salt: diets.some(d => d.intakedietrestrictions_id === 2),
      diet_renal: diets.some(d => d.intakedietrestrictions_id === 3),
      diet_puree: diets.some(d => d.intakedietrestrictions_id === 4),

      entitle_medical: ents.some(e => e.intakeentitlements_id === 1),
      entitle_alwpp: ents.some(e => e.intakeentitlements_id === 2),
      entitle_medical_pending: ents.some(e => e.intakeentitlements_id === 3),
      entitle_va: ents.some(e => e.intakeentitlements_id === 4)
    });
  }

  initForm() {
    this.patientForm = this.fb.group({
      relationship_id: ['', Validators.required],
      first_name: ['', Validators.required],
      last_name: ['', Validators.required],
      dob: ['', [Validators.required, dobValidator]],
      date_of_birth: ['', Validators.required],
      gender: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.required, Validators.pattern(/^[0-9\-+() ]{7,20}$/)]],

      levelofcare_id: ['', Validators.required],
      annual_income: ['', [Validators.required, Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      budget_type: ['', Validators.required],
      budget_min: ['', [Validators.required, Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      budget_max: ['', [Validators.required, Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      height_feet: [0, [Validators.required, Validators.min(0), Validators.pattern(/^\d+$/)]],
      height_inches: [0, [Validators.required, Validators.min(0), Validators.max(11), Validators.pattern(/^\d+$/)]],
      weight: ['0.00', [Validators.required, Validators.min(0), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      accommodation_type_id: [''],
      transfers: ['', Validators.required],
      ambulation: ['', Validators.required],
      bathing: ['', Validators.required],
      eating: ['', Validators.required],
      mental_status: ['', Validators.required],
      incontinence_care_bowel: ['', Validators.required],
      incontinence_care_bladder: ['', Validators.required],
      diabetes: ['', Validators.required],
      dressing: ['', Validators.required],
      dressing_type: ['', Validators.required],
      hospice_imminent: ['', Validators.required],

      need_oxygen: [false],
      need_catheter: [false],
      need_catheter_self: [false],
      need_bedsore: [false],
      need_colostomy: [false],
      need_colostomy_self: [false],

      diet_diabetic: [false],
      diet_low_salt: [false],
      diet_renal: [false],
      diet_puree: [false],

      entitle_medical: [false],
      entitle_alwpp: [false],
      entitle_medical_pending: [false],
      entitle_va: [false],

      diagnosis: ['', Validators.required],
      medication_notes: [''],
      additional_comments: [''],

      agent_show_pii: [false]
    }, { validator: budgetRangeValidator });
  }

  get form() {
    return this.patientForm.controls;
  }

  loadPreIntakeData() {
    this.isLoadingPreIntake = true;
    this.patientService.getPreIntakeData()
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (data) => {
          this.preIntakeData = data;
          this.isLoadingPreIntake = false;

          if (data) {
            this.transfers = data.transfers;
            this.ambulation = data.ambulation;
            this.bathing = data.bathing;
            this.eating = data.eating;
            this.mentalStatus = data.mentalStatus;
            this.incontinencecare = data.incontinencecare;
            this.diabetes = data.diabetes;
            this.dressing = data.dressing;
            this.specialcareNeeds = data.specialcareNeeds;
            this.dietRestrictions = data.dietRestrictions;
            this.entitlements = data.entitlements;
          }
          console.log('Pre-intake API loaded successfully:', this.preIntakeData);
        },
        (err) => {
          this.isLoadingPreIntake = false;
          console.error('Failed to load pre-intake API:', err);
        }
      );
  }

  loadRelationships() {
    this.isLoadingRelationships = true;
    this.patientService.getRelationships()
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (data) => {
          this.relationships = data;
          this.isLoadingRelationships = false;
          console.log('Relationships API loaded successfully:', this.relationships);
        },
        (err) => {
          this.isLoadingRelationships = false;
          console.error('Failed to load relationships API:', err);
        }
      );
  }

  loadLevelOfCares() {
    this.isLoadingData = true;
    this.patientService.getLevelOfCares()
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (data) => {
          this.levelOfCares = data || [];
          this.isLoadingData = false;
        },
        (err) => {
          this.isLoadingData = false;
        }
      );
  }

  loadAccommodationTypes() {
    this.isLoadingAccommodationTypes = true;
    this.patientService.getAccommodationTypes()
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (data) => {
          this.accommodationTypes = data || [];
          this.isLoadingAccommodationTypes = false;
          console.log('Accommodation types loaded:', this.accommodationTypes);
        },
        (err) => {
          this.isLoadingAccommodationTypes = false;
          console.error('Failed to load accommodation types:', err);
        }
      );
  }

  onDateChange(evt: any, control: AbstractControl) {
    if (evt) {
      control.setValue(moment(evt).format('YYYY-MM-DD'));
    }
  }

  savePatient() {
    this.submitted = true;
    if (this.patientForm.invalid) {
      return;
    }

    this.isRequestInprogress = true;
    const formVal = this.patientForm.value;

    const specialcareneeds = [];
    if (formVal.need_oxygen) specialcareneeds.push(1);
    if (formVal.need_catheter) specialcareneeds.push(2);
    if (formVal.need_catheter_self) specialcareneeds.push(3);
    if (formVal.need_bedsore) specialcareneeds.push(4);
    if (formVal.need_colostomy) specialcareneeds.push(5);
    if (formVal.need_colostomy_self) specialcareneeds.push(6);

    const dietrestrictions = [];
    if (formVal.diet_diabetic) dietrestrictions.push(1);
    if (formVal.diet_low_salt) dietrestrictions.push(2);
    if (formVal.diet_renal) dietrestrictions.push(3);
    if (formVal.diet_puree) dietrestrictions.push(4);

    const entitlements = [];
    if (formVal.entitle_medical) entitlements.push(1);
    if (formVal.entitle_alwpp) entitlements.push(2);
    if (formVal.entitle_medical_pending) entitlements.push(3);
    if (formVal.entitle_va) entitlements.push(4);

    const payload = {
      patient: {
        email: formVal.email,
        first_name: encryptField(formVal.first_name),
        last_name: encryptField(formVal.last_name || ''),
        phone: encryptField(formVal.phone),
        date_of_birth: encryptField(formVal.date_of_birth),
        gender: formVal.gender,
        ...(this.memberSummary ? {
          reference_id: this.memberSummary._id || '',
          reference_name: `${this.memberSummary.firstName || ''} ${this.memberSummary.lastName || ''}`.trim(),
          reference_phone: this.memberSummary.cellPhone || ''
        } : {})
      },
      relationship_id: String(formVal.relationship_id),
      levelofcare_id: String(formVal.levelofcare_id),
      intake_status_id: '2',
      diagnosis: encryptField(formVal.diagnosis),
      height_feet: formVal.height_feet !== '' && formVal.height_feet !== null && formVal.height_feet !== undefined ? Number(formVal.height_feet) : 0,
      height_inches: formVal.height_inches !== '' && formVal.height_inches !== null && formVal.height_inches !== undefined ? Number(formVal.height_inches) : 0,
      weight: formVal.weight !== '' && formVal.weight !== null && formVal.weight !== undefined ? Number(formVal.weight).toFixed(2) : '0.00',
      accommodation_type_id: formVal.accommodation_type_id !== '' && formVal.accommodation_type_id !== null && formVal.accommodation_type_id !== undefined ? Number(formVal.accommodation_type_id) : 1,
      annual_income: encryptField(String(formVal.annual_income)),
      budget_type: (formVal.budget_type || ''),
      budget_min: Number(formVal.budget_min),
      budget_max: Number(formVal.budget_max),
      transfers: Number(formVal.transfers),
      ambulation: Number(formVal.ambulation),
      bathing: Number(formVal.bathing),
      eating: Number(formVal.eating),
      mental_status: Number(formVal.mental_status),
      incontinence_care_bowel: Number(formVal.incontinence_care_bowel),
      incontinence_care_bladder: Number(formVal.incontinence_care_bladder),
      diabetes: Number(formVal.diabetes),
      dressing: Number(formVal.dressing),
      dressing_type: (formVal.dressing_type || ''),
      hospice_imminent: (formVal.hospice_imminent || ''),
      medication_notes: encryptField(formVal.medication_notes || ''),
      additional_comments: encryptField(formVal.additional_comments || ''),
      agent_show_pii: formVal.agent_show_pii ? 1 : 0,
      specialcareneeds,
      dietrestrictions,
      entitlements
    };

    if (this.isEditMode) {
      this.patientService.updatePatientRecord(this.patientId, payload)
        .pipe(takeUntil(componentDestroyed(this)))
        .subscribe(
          (data) => {
            this.isRequestInprogress = false;
            sweetAlert('Success', 'Patient and Intake updated successfully', 'success', 'OK').then(() => {
              this.router.navigateByUrl('/placement/patient');
            });
          },
          (err) => {
            this.isRequestInprogress = false;
            sweetAlert('Error', err || 'Failed to update patient', 'warning', 'OK');
          }
        );
    } else {
      this.patientService.createPatient(payload)
        .pipe(takeUntil(componentDestroyed(this)))
        .subscribe(
          (data) => {
            this.isRequestInprogress = false;
            sweetAlert('Success', 'Patient and Intake created successfully', 'success', 'OK').then(() => {
              this.router.navigateByUrl('/placement/patient');
            });
          },
          (err) => {
            this.isRequestInprogress = false;
            sweetAlert('Error', err || 'Failed to create patient', 'warning', 'OK');
          }
        );
    }
  }

  onCreateAssist(): void {
    if (this.refPatientData) {
      const patient = this.refPatientData.e_patients || {};
      const patientId = patient.id || '';
      const patientName = `${decryptField(patient.first_name) || ''} ${decryptField(patient.last_name) || ''}`.trim();
      const intakeId = this.refPatientData.id;

      this.router.navigate(['/placement/assists/create'], {
        queryParams: {
          patientId,
          patientName,
          intakeId
        }
      });
    }
  }

  ngOnDestroy() { }
}
